// Bézier geometry: live shapes → subpaths, subpaths → canvas paths, exact bounds (curve extrema).
import type { Anchor, Rect, Shape, Subpath } from '../model/types';

/** A corner anchor with no handles. */
export const corner = (x: number, y: number): Anchor => ({
  x,
  y,
  inX: x,
  inY: y,
  outX: x,
  outY: y,
  type: 'corner',
});

const K = 0.5522847498307936; // 4/3 · (√2 − 1): cubic approximation of a quarter circle

/** Subpaths of a live shape in its own coordinates (Expand turns a shape into exactly these). */
export function shapeToSubpaths(s: Shape): Subpath[] {
  switch (s.type) {
    case 'rect': {
      const { x, y, w, h } = s;
      const lim = Math.min(Math.abs(w), Math.abs(h)) / 2;
      const c = (r: number) => Math.max(0, Math.min(r, lim));
      const r0 = c(s.radii[0]),
        r1 = c(s.radii[1]),
        r2 = c(s.radii[2]),
        r3 = c(s.radii[3]);
      if (!r0 && !r1 && !r2 && !r3)
        return [
          { closed: true, points: [corner(x, y), corner(x + w, y), corner(x + w, y + h), corner(x, y + h)] },
        ];
      const pts: Anchor[] = [];
      const arc = (cx: number, cy: number, r: number, a0: number) => {
        // Quarter arc from angle a0 to a0 + 90° (y down), as two anchors with handles.
        if (!r) {
          pts.push(corner(cx, cy));
          return;
        }
        const p = (a: number) => [cx + r * Math.cos(a), cy + r * Math.sin(a)] as const;
        const t = (a: number) => [-Math.sin(a), Math.cos(a)] as const;
        const a1 = a0 + Math.PI / 2;
        const [x0, y0] = p(a0),
          [x1, y1] = p(a1);
        const [tx0, ty0] = t(a0),
          [tx1, ty1] = t(a1);
        pts.push({
          x: x0,
          y: y0,
          inX: x0,
          inY: y0,
          outX: x0 + tx0 * r * K,
          outY: y0 + ty0 * r * K,
          type: 'smooth',
        });
        pts.push({
          x: x1,
          y: y1,
          inX: x1 - tx1 * r * K,
          inY: y1 - ty1 * r * K,
          outX: x1,
          outY: y1,
          type: 'smooth',
        });
      };
      arc(x + w - r1, y + r1, r1, -Math.PI / 2); // top-right
      arc(x + w - r2, y + h - r2, r2, 0); // bottom-right
      arc(x + r3, y + h - r3, r3, Math.PI / 2); // bottom-left
      arc(x + r0, y + r0, r0, Math.PI); // top-left
      return [{ closed: true, points: pts }];
    }
    case 'ellipse': {
      const { cx, cy, rx, ry } = s;
      const kx = rx * K,
        ky = ry * K;
      return [
        {
          closed: true,
          points: [
            {
              x: cx + rx,
              y: cy,
              inX: cx + rx,
              inY: cy - ky,
              outX: cx + rx,
              outY: cy + ky,
              type: 'symmetric',
            },
            {
              x: cx,
              y: cy + ry,
              inX: cx + kx,
              inY: cy + ry,
              outX: cx - kx,
              outY: cy + ry,
              type: 'symmetric',
            },
            {
              x: cx - rx,
              y: cy,
              inX: cx - rx,
              inY: cy + ky,
              outX: cx - rx,
              outY: cy - ky,
              type: 'symmetric',
            },
            {
              x: cx,
              y: cy - ry,
              inX: cx - kx,
              inY: cy - ry,
              outX: cx + kx,
              outY: cy - ry,
              type: 'symmetric',
            },
          ],
        },
      ];
    }
    case 'polygon': {
      const n = Math.max(3, Math.round(s.sides));
      return [
        {
          closed: true,
          points: Array.from({ length: n }, (_, i) => {
            const a = -Math.PI / 2 + (i * 2 * Math.PI) / n;
            return corner(s.cx + s.r * Math.cos(a), s.cy + s.r * Math.sin(a));
          }),
        },
      ];
    }
    case 'star': {
      const n = Math.max(3, Math.round(s.points));
      return [
        {
          closed: true,
          points: Array.from({ length: 2 * n }, (_, i) => {
            const a = -Math.PI / 2 + (i * Math.PI) / n;
            const r = i % 2 ? s.r2 : s.r1;
            return corner(s.cx + r * Math.cos(a), s.cy + r * Math.sin(a));
          }),
        },
      ];
    }
    case 'line':
      return [{ closed: false, points: [corner(s.x1, s.y1), corner(s.x2, s.y2)] }];
  }
}

type PathSink = Pick<Path2D, 'moveTo' | 'lineTo' | 'bezierCurveTo' | 'closePath'>;

const isLine = (a: Anchor, b: Anchor) => a.outX === a.x && a.outY === a.y && b.inX === b.x && b.inY === b.y;

/** Appends subpaths to a Path2D / canvas context. */
export function tracePath(p: PathSink, subpaths: Subpath[]) {
  for (const sp of subpaths) {
    const pts = sp.points;
    if (!pts.length) continue;
    p.moveTo(pts[0]!.x, pts[0]!.y);
    const n = sp.closed ? pts.length : pts.length - 1;
    for (let i = 0; i < n; i++) {
      const a = pts[i]!,
        b = pts[(i + 1) % pts.length]!;
      if (isLine(a, b)) p.lineTo(b.x, b.y);
      else p.bezierCurveTo(a.outX, a.outY, b.inX, b.inY, b.x, b.y);
    }
    if (sp.closed) p.closePath();
  }
}

/** Extrema parameters t ∈ (0,1) of one cubic coordinate. */
function cubicExtrema(p0: number, p1: number, p2: number, p3: number): number[] {
  const a = -p0 + 3 * p1 - 3 * p2 + p3,
    b = 2 * (p0 - 2 * p1 + p2),
    c = p1 - p0;
  const out: number[] = [];
  if (Math.abs(a) < 1e-12) {
    if (Math.abs(b) > 1e-12) out.push(-c / b);
  } else {
    const disc = b * b - 4 * a * c;
    if (disc >= 0) {
      const sq = Math.sqrt(disc);
      out.push((-b + sq) / (2 * a), (-b - sq) / (2 * a));
    }
  }
  return out.filter((t) => t > 0 && t < 1);
}
const cubicAt = (p0: number, p1: number, p2: number, p3: number, t: number) => {
  const u = 1 - t;
  return u * u * u * p0 + 3 * u * u * t * p1 + 3 * u * t * t * p2 + t * t * t * p3;
};

/** Exact bounds of subpaths (anchors + curve extrema), or null when empty. */
export function subpathBounds(subpaths: Subpath[]): Rect | null {
  let x0 = Infinity,
    y0 = Infinity,
    x1 = -Infinity,
    y1 = -Infinity;
  const add = (x: number, y: number) => {
    if (x < x0) x0 = x;
    if (x > x1) x1 = x;
    if (y < y0) y0 = y;
    if (y > y1) y1 = y;
  };
  for (const sp of subpaths) {
    const pts = sp.points;
    const n = sp.closed ? pts.length : pts.length - 1;
    if (pts.length) add(pts[0]!.x, pts[0]!.y);
    for (let i = 0; i < n; i++) {
      const a = pts[i]!,
        b = pts[(i + 1) % pts.length]!;
      add(b.x, b.y);
      if (isLine(a, b)) continue;
      for (const t of cubicExtrema(a.x, a.outX, b.inX, b.x))
        add(cubicAt(a.x, a.outX, b.inX, b.x, t), cubicAt(a.y, a.outY, b.inY, b.y, t));
      for (const t of cubicExtrema(a.y, a.outY, b.inY, b.y))
        add(cubicAt(a.x, a.outX, b.inX, b.x, t), cubicAt(a.y, a.outY, b.inY, b.y, t));
    }
  }
  return x0 === Infinity ? null : { x: x0, y: y0, w: x1 - x0, h: y1 - y0 };
}
